import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import ENV from 'bullitt-time-cloud-live/config/environment';

const STORAGE_KEY_CODE = 'bullitttime-access-code';
const STORAGE_KEY_SESSION = 'bullitttime-session-id';

export default class AuthService extends Service {
  @tracked accessCode = null;
  @tracked sessionId = null;
  @tracked isValidating = false;
  @tracked validationError = null;

  constructor() {
    super(...arguments);
    // Load saved code and session from localStorage on initialization
    this.loadSavedCredentials();
  }

  get isAuthenticated() {
    return !!this.accessCode;
  }

  get apiEndpoint() {
    return ENV.APP.CLOUD_REST_API_ENDPOINT;
  }

  /**
   * Get the authorization token (code:sessionId format).
   */
  get authToken() {
    if (!this.accessCode) return null;
    if (!this.sessionId) return this.accessCode;
    return `${this.accessCode}:${this.sessionId}`;
  }

  /**
   * Generate a random session ID (UUID-like).
   */
  generateSessionId() {
    // Generate a random UUID-like string
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Load access code and session ID from localStorage.
   */
  loadSavedCredentials() {
    try {
      const savedCode = localStorage.getItem(STORAGE_KEY_CODE);
      const savedSession = localStorage.getItem(STORAGE_KEY_SESSION);

      if (savedCode) {
        this.accessCode = savedCode;
        console.log('[Auth] Loaded saved access code');
      }

      if (savedSession) {
        this.sessionId = savedSession;
        console.log('[Auth] Loaded saved session ID');
      } else if (savedCode) {
        // Have code but no session - generate new session
        this.sessionId = this.generateSessionId();
        localStorage.setItem(STORAGE_KEY_SESSION, this.sessionId);
        console.log('[Auth] Generated new session ID');
      }
    } catch (error) {
      console.error('[Auth] Error loading saved credentials:', error);
    }
  }

  /**
   * Save access code and session ID to localStorage.
   */
  saveCredentials(code) {
    try {
      // Generate new session ID for new login
      const sessionId = this.generateSessionId();

      localStorage.setItem(STORAGE_KEY_CODE, code);
      localStorage.setItem(STORAGE_KEY_SESSION, sessionId);

      this.accessCode = code;
      this.sessionId = sessionId;

      console.log('[Auth] Credentials saved (new session)');
    } catch (error) {
      console.error('[Auth] Error saving credentials:', error);
    }
  }

  /**
   * Clear access code and session ID from localStorage.
   */
  clearCredentials() {
    try {
      localStorage.removeItem(STORAGE_KEY_CODE);
      localStorage.removeItem(STORAGE_KEY_SESSION);
      this.accessCode = null;
      this.sessionId = null;
      console.log('[Auth] Credentials cleared');
    } catch (error) {
      console.error('[Auth] Error clearing credentials:', error);
    }
  }

  /**
   * Validate an access code by making an API request.
   * Returns true if valid, false otherwise.
   */
  async validateCode(code) {
    this.isValidating = true;
    this.validationError = null;

    // Generate a session ID for validation
    const tempSessionId = this.generateSessionId();

    try {
      // Make a request to the events endpoint with the code and session
      const response = await fetch(`${this.apiEndpoint}/events`, {
        headers: {
          'Authorization': `Bearer ${code}:${tempSessionId}`
        }
      });

      if (response.ok) {
        // Code is valid - save credentials with this session
        localStorage.setItem(STORAGE_KEY_CODE, code);
        localStorage.setItem(STORAGE_KEY_SESSION, tempSessionId);
        this.accessCode = code;
        this.sessionId = tempSessionId;
        console.log('[Auth] Login successful');
        return true;
      }

      if (response.status === 401 || response.status === 403) {
        this.validationError = 'Invalid or expired access code';
        return false;
      }

      this.validationError = `Unexpected error: ${response.status}`;
      return false;
    } catch (error) {
      console.error('[Auth] Validation error:', error);
      this.validationError = 'Unable to validate code. Please check your connection.';
      return false;
    } finally {
      this.isValidating = false;
    }
  }

  /**
   * Check if the current saved code is still valid.
   * Returns true if valid, false if invalid or no code saved.
   */
  async checkCurrentCode() {
    if (!this.accessCode || !this.sessionId) {
      return false;
    }

    this.isValidating = true;
    this.validationError = null;

    try {
      const response = await fetch(`${this.apiEndpoint}/events`, {
        headers: {
          'Authorization': `Bearer ${this.authToken}`
        }
      });

      if (response.ok) {
        return true;
      }

      if (response.status === 401 || response.status === 403) {
        // Code is no longer valid - clear it
        console.log('[Auth] Saved credentials are no longer valid');
        this.clearCredentials();
        return false;
      }

      // Other errors - don't clear the code, might be a temporary issue
      console.error('[Auth] Error checking code:', response.status);
      return false;
    } catch (error) {
      console.error('[Auth] Error checking code:', error);
      // Network error - don't clear the code
      return false;
    } finally {
      this.isValidating = false;
    }
  }

  /**
   * Login with an access code.
   * Returns true if successful, false otherwise.
   */
  async login(code) {
    return this.validateCode(code);
  }

  /**
   * Logout - clear the access code and session.
   */
  logout() {
    this.clearCredentials();
  }

  /**
   * Handle an authentication failure (e.g., 401/403 response).
   * Clears the credentials.
   */
  handleAuthFailure() {
    console.log('[Auth] Authentication failure - clearing credentials');
    this.clearCredentials();
  }
}
